import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, type CraneSwingHazard } from '../../../hazards/HazardDefinition';
import { hazardSocket, sidesWithout } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_CRANE_SWING_20M – one swinging crane load per 20 m module (OBS_CRANE_LOAD_B as a pendulum, the same rig and
 * geometry as z 28 of SEG_CRANE_40M: load at the module centre, the tower crane 6 m behind it and 13 m beside the
 * track). Its chain rule puts 2–4 of them in a row (user 2026-10-03: "die Kran-Hindernisse, die schwenken … zwei, drei,
 * vier hintereinander, aber so, dass genug Platz ist zum Hin- und Herrennen"): 20 m from load to load = 1.1 s at top
 * speed, reaction and two lane changes take 0.78 s – the sequence validator leaves out a load that would not fit the
 * one before it.
 *
 * Each module draws its own layout, so the runner zig-zags through the run:
 *   lingering over the left / right lane (turning point there) · sweeping through the centre lane to the right / left
 *   – each with its crane on the right or the left of the track.
 * No ground obstacles: dodging the loads is the task.
 */
export const CRANE_SWING = {
  length: 20,
  load: { z: 10 },
  crane: { x: 13, z: 16 },
} as const;

const W = WORLD_CONFIG;
const SOCKET = 'SOCKET_EVT_CRANE_SWING';
/** The load sweeps through the centre lane in half of the layouts' weight, it turns over an outer lane in the rest. */
const THROUGH_WEIGHT = 0.5;

const swing = (id: string, lane: number, craneSide: 1 | -1, sweep?: 1 | -1): CraneSwingHazard => ({
  id,
  kind: HazardKind.CraneSwing,
  socketId: SOCKET,
  lane,
  ...(sweep ? { sweep } : {}),
  z: CRANE_SWING.load.z,
  crane: { x: craneSide * CRANE_SWING.crane.x, z: CRANE_SWING.crane.z },
});

const HAZARDS: readonly CraneSwingHazard[] = [
  swing('HAZ_SWING_L0_R', 0, 1),
  swing('HAZ_SWING_L2_R', 2, 1),
  swing('HAZ_SWING_L1_RIGHT_R', 1, 1, 1),
  swing('HAZ_SWING_L1_LEFT_R', 1, 1, -1),
  swing('HAZ_SWING_L0_L', 0, -1),
  swing('HAZ_SWING_L2_L', 2, -1),
  swing('HAZ_SWING_L1_RIGHT_L', 1, -1, 1),
  swing('HAZ_SWING_L1_LEFT_L', 1, -1, -1),
];

const VARIANTS: ObstacleVariant[] = HAZARDS.map((hazard) => ({
  id: hazard.id.replace('HAZ_', ''),
  weight: hazard.lane === 1 ? THROUGH_WEIGHT : 1,
  placements: [],
  hazards: [hazard.id],
}));

// The crane stands right or left at z 16: no backdrop building on either side, the roadside bays at z 15 shrink to
// single ground props (as beside the swing crane of SEG_CRANE_40M).
const SIDES = sidesWithout(CRANE_SWING.length, ['SOCKET_DECO_L_04', 'SOCKET_DECO_R_04'], ['SOCKET_DECO_L_02', 'SOCKET_DECO_R_02']);

export const SEG_CRANE_SWING_20M: SegmentDefinition = {
  id: 'SEG_CRANE_SWING_20M',
  kind: SegmentKind.Straight,
  displayName: 'Pendelkran 20 m',
  length: CRANE_SWING.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',

  entry: entryConnector(),
  exits: [straightExit(CRANE_SWING.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: VARIANTS,
  hazards: HAZARDS,

  collectibleSpawnPoints: collectibleLanes(W, 1, CRANE_SWING.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  decorationSockets: SIDES.decorationSockets,
  advertisingSlots: SIDES.advertisingSlots,
  dynamicEventSockets: [...SIDES.dynamicEventSockets, hazardSocket(SOCKET, 0, CRANE_SWING.load.z, DynamicEventType.CraneLoadSwing)],

  generation: {
    weight: 0.2,
    minDistance: 250,  // was 500 – user 2026-10-05: much harder from early on
    // from 1200 m the loads come closer together: SEG_CRANE_SWING_60M / _60M_TIGHT (SEG_CRANE_SWING_RUN.ts) – user
    // 2026-10-05 late: "ein recht kurzer Abstand … unter den schwingenden Kränen … wirklich nicht leicht" (was 3000 m)
    maxDistance: 1200,
    minSegmentsAfterJunction: 1,
    // never right behind the crane area (or a finished swing run) – only inside its own run
    family: 'CRANE',
    chain: {
      lengths: [
        [2, 0.4],
        [3, 0.35],
        [4, 0.25],
      ],
      // user 2026-10-04: "die schwenkenden Kräne mehrfach bringen … dichter … genauso nach 900 bis 1000 m" – longer runs
      // early (their pick weight rises with DifficultyTier.hazardWeightScale as well)
      from: [
        { distance: 500, lengths: [[3, 0.3], [4, 0.35], [5, 0.35]] },
        { distance: 3000, lengths: [[4, 0.3], [5, 0.4], [6, 0.3]] },
      ],
    },
    tags: ['crane', 'hazard', 'lane-change'],
  },
};
